import React, { useState, useEffect } from 'react';
import { useNavigate, Link } from 'react-router-dom';
import { Eye, EyeOff, Mail, User, Loader } from 'lucide-react';
import axios from 'axios';
import './signup.css';
import { useAuth } from '../contexts/AuthContext';

function Signup() {
  const { login } = useAuth();
  const [formData, setFormData] = useState({
    name: '',
    email: '',
    password: '',
    confirmPassword: ''
  });

  const [showPassword, setShowPassword] = useState(false);
  const [showConfirmPassword, setShowConfirmPassword] = useState(false);
  const [error, setError] = useState('');
  const [isLoading, setIsLoading] = useState(false);
  const navigate = useNavigate();

  const testServerConnection = async () => {
    try {
      const response = await axios.get('https://swanpointb.onrender.com/test');
      console.log('Server test response:', response.data);
      return true;
    } catch (error) {
      console.error('Server test error:', error);
      return false;
    }
  };

  // Initialize Google OAuth
  useEffect(() => {
    // Remove any existing script first
    const existingScript = document.getElementById('google-signin-script');
    if (existingScript) {
      existingScript.remove();
    }

    testServerConnection().then(isConnected => {
      if (!isConnected) {
        console.warn('Unable to reach server');
      } else {
        console.log('Server is reachable');
      }
    });

    const script = document.createElement('script');
    script.src = 'https://accounts.google.com/gsi/client';
    script.id = 'google-signin-script';
    script.async = true;
    script.defer = true;
    
    script.onload = () => {
      if (window.google) {
        window.google.accounts.id.initialize({
          client_id: "131409474575-dafvnt64fovcklq6sq8t1t6mikgkojho.apps.googleusercontent.com",
          callback: handleGoogleResponse,
          auto_select: false,
          cancel_on_tap_outside: true,
          context: 'signup'
        });

        window.google.accounts.id.renderButton(
          document.getElementById("google-signin"),
          { 
            type: 'standard',
            theme: 'filled_black',
            size: 'large',
            text: 'signup_with',
            shape: 'rectangular',
            logo_alignment: 'left',
            width: '100%'
          }
        );
      }
    };

    document.body.appendChild(script);

    return () => {
      const script = document.getElementById('google-signin-script');
      if (script) {
        script.remove();
      }
      // Cleanup Google OAuth
      if (window.google) {
        window.google.accounts.id.cancel();
      }
    };
  },);

  const validatePassword = (password) => {
    const minLength = 8;
    const hasUpperCase = /[A-Z]/.test(password);
    const hasLowerCase = /[a-z]/.test(password);
    const hasNumbers = /\d/.test(password);
    const hasSpecialChar = /[!@#$%^&*(),.?":{}|<>]/.test(password);

    if (password.length < minLength) {
      return "Password must be at least 8 characters long";
    }
    if (!hasUpperCase) {
      return "Password must contain at least one uppercase letter";
    }
    if (!hasLowerCase) {
      return "Password must contain at least one lowercase letter";
    }
    if (!hasNumbers) {
      return "Password must contain at least one number";
    }
    if (!hasSpecialChar) {
      return "Password must contain at least one special character";
    }
    return "";
  };

  const handleChange = (e) => {
    const { name, value } = e.target;
    setFormData(prev => ({
      ...prev,
      [name]: value
    }));
    setError('');

    // Validate password as user types
    if (name === 'password') {
      const passwordError = validatePassword(value);
      if (passwordError) {
        setError(passwordError);
      }
    }

    // Check password match as user types confirm password
    if (name === 'confirmPassword') {
      if (value !== formData.password) {
        setError('Passwords do not match');
      }
    }
  };

  const handleGoogleResponse = async (response) => {
    try {
      setIsLoading(true);
      setError('');

      // Decode the JWT token from Google
      const token = response.credential;
      const base64Url = token.split('.')[1];
      const base64 = base64Url.replace(/-/g, '+').replace(/_/g, '/');
      const jsonPayload = decodeURIComponent(atob(base64).split('').map(c => {
        return '%' + ('00' + c.charCodeAt(0).toString(16)).slice(-2);
      }).join(''));

      const { email, name, picture } = JSON.parse(jsonPayload);

      // Send to your backend
      const backendResponse = await axios.post('http://localhost:5000/api/auth/google', {
        token,
        email,
        name,
        picture
      });

      if (backendResponse.data.token) {
        localStorage.setItem('token', backendResponse.data.token);
        navigate('/investor');
      } else {
        throw new Error('No token received from server');
      }
    } catch (err) {
      console.error('Google Sign-In Error:', err);
      setError(err.response?.data?.message || 'Failed to sign in with Google. Please try again.');
    } finally {
      setIsLoading(false);
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError('');
    setIsLoading(true);

    // Validate password match
    if (formData.password !== formData.confirmPassword) {
      setError('Passwords do not match');
      setIsLoading(false);
      return;
    }

    // Validate password strength
    const passwordError = validatePassword(formData.password);
    if (passwordError) {
      setError(passwordError);
      setIsLoading(false);
      return;
    }
  
    try {
      const response = await axios({
        method: 'POST',
        url: 'https://swanpointb.onrender.com/api/auth/signup'|| 'http://localhost:5000/api/auth/signup',
        headers: {
          'Content-Type': 'application/json'
        },
        data: {
          name: formData.name,
          email: formData.email,
          password: formData.password
        }
      });
  
      if (response.data.success) {
        // Store token and user data
        localStorage.setItem('token', response.data.token);
        localStorage.setItem('user', JSON.stringify(response.data.user));
        
        // Update auth context
        login(response.data.user, response.data.token);
        
        // Navigate to dashboard
        console.log("Navigation to investor page");
        navigate('/investor');
      } else {
        throw new Error(response.data.message || 'Signup failed');
      }
    } catch (error) {
      console.error('Signup error:', error);
      setError(
        error.response?.data?.message || 
        error.message || 
        'Failed to create account. Please try again.'
      );
    } finally {
      setIsLoading(false);
    }
  };

  return (
    <div className="auth-container">
      <div className="auth-content">
        <h1 className="auth-title">Create Account</h1>
        <p className="auth-subtitle">Join Swanpoint and start your investment journey</p>

        <div id="google-signin" className="google-button"></div>

        <div className="auth-divider">
          <span>Or continue with email</span>
        </div>

        <form onSubmit={handleSubmit} className="auth-form">
          <div className="form-group">
            <label htmlFor="name">Name</label>
            <div className="input-wrapper">
              <input
                type="text"
                id="name"
                name="name"
                value={formData.name}
                onChange={handleChange}
                placeholder="Enter your full name"
                required
                disabled={isLoading}
                minLength={2}
                maxLength={50}
              />
              <User className="input-icon" size={20} />
            </div>
          </div>

          <div className="form-group">
            <label htmlFor="email">Email</label>
            <div className="input-wrapper">
              <input
                type="email"
                id="email"
                name="email"
                value={formData.email.toLowerCase()}
                onChange={handleChange}
                placeholder="Enter your email address"
                required
                disabled={isLoading}
                pattern="[a-z0-9._%+-]+@[a-z0-9.-]+\.[a-z]{2,}$"
              />
              <Mail className="input-icon" size={20} />
            </div>
          </div>

          <div className="form-group">
            <label htmlFor="password">Password</label>
            <div className="input-wrapper">
              <input
                type={showPassword ? "text" : "password"}
                id="password"
                name="password"
                value={formData.password}
                onChange={handleChange}
                placeholder="Create a strong password"
                required
                disabled={isLoading}
                minLength={8}
              />
              <button
                type="button"
                className="password-toggle"
                onClick={() => setShowPassword(!showPassword)}
                disabled={isLoading}
              >
                {showPassword ? <EyeOff size={20} /> : <Eye size={20} />}
              </button>
            </div>
          </div>

          <div className="form-group">
            <label htmlFor="confirmPassword">Confirm Password</label>
            <div className="input-wrapper">
              <input
                type={showConfirmPassword ? "text" : "password"}
                id="confirmPassword"
                name="confirmPassword"
                value={formData.confirmPassword}
                onChange={handleChange}
                placeholder="Confirm your password"
                required
                disabled={isLoading}
              />
              <button
                type="button"
                className="password-toggle"
                onClick={() => setShowConfirmPassword(!showConfirmPassword)}
                disabled={isLoading}
              >
                {showConfirmPassword ? <EyeOff size={20} /> : <Eye size={20} />}
              </button>
            </div>
          </div>

          {error && (
            <div className="error-message">
              {error}
            </div>
          )}

          <button 
            type="submit" 
            className={`submit-button ${isLoading ? 'loading' : ''}`}
            disabled={isLoading}
          >
            {isLoading ? (
              <>
                <Loader className="spinner" />
                Creating Account...
              </>
            ) : (
              'Create Account'
            )}
          </button>
        </form>

        <p className="auth-footer">
          Already have an account?{' '}
          <Link to="/login" className="auth-link">
            Sign in
          </Link>
        </p>
      </div>
    </div>
  );
}

export default Signup;